import { Typography } from "@avenue-8/sidekick-ui";
import {
  PropertyPhoto,
  SideBySideComparisonSectionView,
  SideBySideComparisonItem,
} from "@cma/presentation-generation-logic";
import { ComparisonPropertyItem } from "./components/ComparisonPropertyItem";
import { useMemo } from "react";
import { PresentationSectionViewProps } from "../PresentationSectionProps";
import { PrintPaper, PrintPage } from "../../shared/Print.styles";
import styled from "@emotion/styled";
import { ComparisonPropertyItemLabels } from "./components/ComparisonPropertyItemLabels";

interface Props
  extends PresentationSectionViewProps<SideBySideComparisonSectionView> {
  galleries: { [key: string]: PropertyPhoto[] };
}

const PrintPropertiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  margin: 0 auto;
  justify-items: center;
`;

export const SideBySideComparisonPrint = (props: Props) => {
  const {
    galleries,
    sectionView: { preferences, properties },
  } = props;

  const { subjectProperty, comparableProperties } = useMemo(() => {
    if (!properties) return { subjectProperty: null, comparableProperties: [] };
    if (properties.length === 0)
      return { subjectProperty: null, comparableProperties: [] };
    const comparableProperties = properties.slice(1);
    const subjectProperty = properties[0];
    return { subjectProperty, comparableProperties };
  }, [properties]);

  const pages = useMemo(() => {
    const allPages: SideBySideComparisonItem[][] = [];
    const itemsPerPage = 4;
    const sliceEndIndex = itemsPerPage - 1;

    if (!subjectProperty) return allPages;

    const firstPageItems: SideBySideComparisonItem[] = [
      subjectProperty,
      ...comparableProperties.slice(0, sliceEndIndex),
    ];
    allPages.push(firstPageItems);

    const remainingComparables = comparableProperties.slice(sliceEndIndex);
    for (let i = 0; i < remainingComparables.length; i += itemsPerPage) {
      allPages.push(remainingComparables.slice(i, i + itemsPerPage));
    }

    return allPages;
  }, [subjectProperty, comparableProperties]);

  return (
    <>
      {pages.map((pageProperties, pageIndex) => (
        <PrintPaper key={`comparison-page-${pageIndex}`}>
          <PrintPage>
            <Typography variant="h5" mb={2} textAlign={"center"}>
              Comparable Properties
            </Typography>

            <PrintPropertiesGrid>
              <ComparisonPropertyItemLabels preferences={preferences} />
              {pageProperties.map((property) => (
                <ComparisonPropertyItem
                  key={property.id}
                  propertyData={property}
                  gallery={galleries[property.id]}
                  isSubjectProperty={property.id === subjectProperty?.id}
                  preferences={preferences}
                  hideLabels={true}
                />
              ))}
            </PrintPropertiesGrid>
          </PrintPage>
        </PrintPaper>
      ))}
    </>
  );
};
